import { useFormik } from 'formik';
import React, { useContext, useMemo, useState } from 'react';
import { DefaultContext } from '../../../../contexts/default';
import ComplementDB from '../../../../database/wrappers/complement';
import ProductDB from '../../../../database/wrappers/product';
import Money from '../../../../helpers/utils/money/money';
import PreAlert from '../../../../helpers/utils/preAlert';
import { convertTrybuIntToPTBR, TRIBUTARY_SITUATION } from '../../../../types/fiscal';
import Button from '../../../bootstrap/Button';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../../bootstrap/Modal';
import FormGroup from '../../../bootstrap/forms/FormGroup';
import Input from '../../../bootstrap/forms/Input';
import Select from '../../../bootstrap/forms/Select';
import Spinner from '../../../bootstrap/Spinner';

const decimalFields = ['aliq_icms', 'pis', 'cofins', 'percentualReducao', 'pRedICMS'];
const normalizeDecimal = value => String(value ?? '').trim().replace(',', '.');

const getInitialValues = (product, productSelected) => {
  const fiscalData = product?.fiscal_data || {};
  return {
    name: product?.name || '',
    price: productSelected?.price || '',
    amount: productSelected?.amount || '',
    bar_code: fiscalData.bar_code || '',
    ncm: fiscalData.ncm || '',
    cst: fiscalData.cst || '',
    csosn: fiscalData.csosn || '',
    cest: fiscalData.cest || '',
    cfop: fiscalData.cfop || '',
    tributary_situation: String(fiscalData.tributary_situation ?? ''),
    aliq_icms: fiscalData.aliq_icms || '',
    pis: fiscalData.pis || '',
    cofins: fiscalData.cofins || '',
    cBenef: fiscalData.cBenef || '',
    cClassTrib: fiscalData.cClassTrib || '',
    cst_cbs_ibs: fiscalData.cst_cbs_ibs || '',
    percentualReducao: fiscalData.percentualReducao || '',
    pRedICMS: fiscalData.pRedICMS || '',
  };
};

const validate = values => {
  const errors = {};
  if (!/^\d{8}$/.test(String(values.ncm).trim())) errors.ncm = 'Informe um NCM com 8 dígitos';
  if (!/^\d{2}$/.test(String(values.cst).trim())) errors.cst = 'Informe um CST com 2 dígitos';
  if (!/^\d{1,3}$/.test(String(values.csosn).trim())) errors.csosn = 'Informe um CSOSN válido';
  if (!/^\d{7}$/.test(String(values.cest).trim())) errors.cest = 'Informe um CEST com 7 dígitos';
  if (!/^\d{4}$/.test(String(values.cfop).trim())) errors.cfop = 'Informe um CFOP com 4 dígitos';

  if (!['0', '1', '2'].includes(String(values.tributary_situation))) {
    errors.tributary_situation = 'Selecione a situação tributária';
  }

  decimalFields.forEach(field => {
    const rawValue = normalizeDecimal(values[field]);
    if (field === 'aliq_icms' && rawValue === '') {
      errors[field] = 'Informe a alíquota do ICMS';
      return;
    }
    if (rawValue === '') return;
    const numberValue = Number(rawValue);
    if (!Number.isFinite(numberValue) || numberValue < 0 || numberValue > 100) {
      errors[field] = 'Informe um número entre 0 e 100';
    }
  });

  if (values.cClassTrib && !/^\d{6}$/.test(String(values.cClassTrib).trim())) {
    errors.cClassTrib = 'Informe 6 dígitos';
  }
  if (values.cst_cbs_ibs && !/^\d{3}$/.test(String(values.cst_cbs_ibs).trim())) {
    errors.cst_cbs_ibs = 'Informe 3 dígitos';
  }
  return errors;
};

const ModalEditProduct = ({ productSelected, open, setIsOpen, setIsClose, productsDicionary, complementsDicionary, estabId }) => {
  const { onShowAlert } = useContext(DefaultContext);
  const [isLoading, setIsLoading] = useState(false);

  const product = useMemo(() => {
    if (!productSelected) return null;
    if (productsDicionary?.[productSelected.id]) {
      return { ...productsDicionary[productSelected.id], isProduct: true, id: productSelected.id };
    }
    if (complementsDicionary?.[productSelected.id]) {
      return { ...complementsDicionary[productSelected.id], isComplement: true, id: productSelected.id };
    }
    return null;
  }, [productSelected, productsDicionary, complementsDicionary]);

  const initialValues = useMemo(
    () => getInitialValues(product, productSelected),
    [product, productSelected],
  );

  const situationOptions = useMemo(
    () => Object.values(TRIBUTARY_SITUATION).map(value => ({
      value: String(value),
      text: convertTrybuIntToPTBR(value),
    })),
    [],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validate,
    onSubmit: async values => {
      if (!product) return;
      setIsLoading(true);

      const fiscalData = {
        ...(product.fiscal_data || {}),
        bar_code: String(values.bar_code || '').trim(),
        ncm: String(values.ncm).trim(),
        cst: String(values.cst).trim().padStart(2, '0'),
        csosn: String(values.csosn).trim().padStart(2, '0'),
        cest: String(values.cest).trim(),
        cfop: String(values.cfop).trim(),
        tributary_situation: Number(values.tributary_situation),
        aliq_icms: normalizeDecimal(values.aliq_icms),
        pis: normalizeDecimal(values.pis),
        cofins: normalizeDecimal(values.cofins),
        cBenef: String(values.cBenef || '').trim(),
        cClassTrib: String(values.cClassTrib || '').trim(),
        cst_cbs_ibs: String(values.cst_cbs_ibs || '').trim(),
        percentualReducao: normalizeDecimal(values.percentualReducao),
        pRedICMS: normalizeDecimal(values.pRedICMS),
      };

      const db = product.isProduct
        ? new ProductDB(estabId)
        : product.isComplement
          ? new ComplementDB(estabId)
          : null;

      if (!db) {
        setIsLoading(false);
        onShowAlert(PreAlert.error('Produto ou complemento não encontrado.'));
        return;
      }

      try {
        await db.update(product.id, { fiscal_data: fiscalData });
        onShowAlert(PreAlert.success('Dados fiscais atualizados com sucesso.'));
        setIsClose();
      } catch (error) {
        console.error('[FixFiscalReports] Falha ao atualizar dados fiscais', error);
        onShowAlert(PreAlert.error('Houve um erro ao atualizar os dados fiscais.'));
      } finally {
        setIsLoading(false);
      }
    },
  });

  const field = (id, label, className = 'col-md-4 mb-3', placeholder = '') => (
    <FormGroup id={id} label={label} className={className}>
      <Input
        name={id}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[id]}
        isValid={formik.isValid}
        isTouched={formik.touched[id]}
        invalidFeedback={formik.errors[id]}
        placeholder={placeholder}
      />
    </FormGroup>
  );

  return (
    <Modal
      id='modal-request'
      titleId='request'
      isOpen={open}
      setIsOpen={setIsOpen}
      isStaticBackdrop
      isScrollable
      isCentered
      size='lg'
      isAnimation>
      <ModalHeader setIsOpen={setIsOpen}>
        <ModalTitle id='register-request'>Correção fiscal do produto</ModalTitle>
      </ModalHeader>
      <ModalBody className='p-4'>
        <form onSubmit={formik.handleSubmit}>
          <div className='d-flex flex-column align-items-center col-md-12'>
            <img src={product?.image_url || ''} alt='foto do produto' height={100} width={100} />
            <h3 style={{ marginTop: 20, marginBottom: 20 }}>{product?.name}</h3>
          </div>

          <div className='row'>
            <FormGroup id='price' label='Preço (UN)' className='col-md-6 mb-3'>
              <Input value={Money.centsToMaskMoney(productSelected?.price)} disabled />
            </FormGroup>
            <FormGroup id='amount' label='Quantidade' className='col-md-6 mb-3'>
              <Input value={productSelected?.amount} disabled />
            </FormGroup>

            {field('bar_code', 'Código de barras', 'col-md-4 mb-3', 'SEM GTIN ou código')}
            {field('ncm', 'NCM', 'col-md-4 mb-3', '8 dígitos')}
            {field('cest', 'CEST', 'col-md-4 mb-3', '7 dígitos')}
            {field('cst', 'CST / ICMS', 'col-md-4 mb-3', 'Ex: 00')}
            {field('csosn', 'CSOSN', 'col-md-4 mb-3', 'Ex: 00')}
            {field('cfop', 'CFOP', 'col-md-4 mb-3', '4 dígitos')}

            <FormGroup id='tributary_situation' label='Situação tributária' className='col-md-6 mb-3'>
              <Select
                name='tributary_situation'
                list={situationOptions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.tributary_situation}
                isValid={formik.isValid}
                isTouched={formik.touched.tributary_situation}
                invalidFeedback={formik.errors.tributary_situation}
              />
            </FormGroup>
            {field('aliq_icms', 'Alíquota ICMS', 'col-md-6 mb-3', 'Ex: 4')}
            {field('pis', 'PIS', 'col-md-6 mb-3', 'Ex: 0.65')}
            {field('cofins', 'COFINS', 'col-md-6 mb-3', 'Ex: 3')}
            {field('cBenef', 'Código de benefício fiscal', 'col-md-6 mb-3')}
            {field('cClassTrib', 'Classificação Tributária CBS/IBS', 'col-md-6 mb-3', '6 dígitos')}
            {field('cst_cbs_ibs', 'CST CBS/IBS', 'col-md-6 mb-3', '3 dígitos')}
            {field('percentualReducao', 'Redução IBS/CBS', 'col-md-6 mb-3')}
            {field('pRedICMS', 'Redução da Base do ICMS (%)', 'col-md-6 mb-3')}
          </div>

          <div className='d-flex justify-content-between mt-3'>
            {isLoading ? (
              <Button color='success' rounded={1} hoverShadow='sm' shadow='sm' disabled>
                <Spinner color='light' inButton isGrow isSmall size={10} tag='span'>Salvando...</Spinner>
                Salvando...
              </Button>
            ) : (
              <Button color='success' icon='check' rounded={1} shadow='sm' hoverShadow='sm' size='sm' type='submit'>
                Confirmar
              </Button>
            )}

            <Button color='danger' icon='cancel' rounded={1} onClick={setIsClose} shadow='sm' hoverShadow='sm' size='sm'>
              Fechar
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ModalEditProduct;
